import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { firstNameOf, initialsOf } from "../../lib/profile";

const UserIcon = ({ color = "currentColor" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const BagIcon = ({ color = "currentColor" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 8h14l-1 13H6L5 8z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
  </svg>
);

const menuItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  width: "100%",
  padding: "0.65rem 0.75rem",
  borderRadius: "8px",
  fontSize: "0.95rem",
  fontFamily: "inherit",
  color: "#000",
  textDecoration: "none",
  background: "none",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
};

// The "Sign in" button is hidden until accounts launch. /login still works if
// you type it in, and once signed in the account menu shows as usual.
export const SHOW_SIGN_IN = false;

// Right-hand end of the desktop header: "Sign in" when signed out, otherwise
// an initials chip that opens a small account menu.
const AccountMenu = () => {
  const { user, profile, loading, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  if (loading) return SHOW_SIGN_IN ? <div style={{ width: "96px" }} /> : null;

  if (!user) {
    if (!SHOW_SIGN_IN) return null;
    const onAuthPage = ["/login", "/signup"].includes(location.pathname);
    return (
      <Link
        to="/login"
        state={{ from: onAuthPage ? undefined : location.pathname }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.5rem 1.1rem",
          borderRadius: "999px",
          fontWeight: 600,
          textDecoration: "none",
          whiteSpace: "nowrap",
          color: onAuthPage ? "#000" : "white",
          backgroundColor: onAuthPage ? "white" : "transparent",
          border: onAuthPage ? "1px solid white" : "1px solid rgba(255,255,255,0.6)",
        }}
      >
        <UserIcon />
        Sign in
      </Link>
    );
  }

  const name = profile?.full_name || user.user_metadata?.full_name;

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    navigate("/", { replace: true });
  };

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          padding: "4px 14px 4px 4px",
          borderRadius: "999px",
          backgroundColor: "rgba(255,255,255,0.16)",
          border: "1px solid rgba(255,255,255,0.35)",
          color: "white",
          fontFamily: "inherit",
          fontWeight: 600,
          fontSize: "0.95rem",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            backgroundColor: "#22d3ee",
            color: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.8rem",
            fontWeight: 700,
          }}
        >
          {initialsOf(name, user.email)}
        </span>
        {firstNameOf(name, user.email)}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ transform: open ? "none" : "rotate(180deg)" }}
        >
          <path d="M6 15l6-6 6 6" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 10px)",
            width: "260px",
            padding: "8px",
            borderRadius: "14px",
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            boxShadow: "0 18px 40px rgba(0,0,0,0.14)",
            color: "#000",
          }}
        >
          <div style={{ padding: "0.75rem 0.75rem 0.85rem" }}>
            {name && <div style={{ fontWeight: 600 }}>{name}</div>}
            <div
              style={{
                fontSize: "0.875rem",
                color: "#666",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user.email}
            </div>
          </div>
          <div style={{ height: "1px", backgroundColor: "#ededed" }} />
          <div style={{ padding: "6px 0" }}>
            <Link to="/account" role="menuitem" style={menuItemStyle}>
              <BagIcon color="#000" />
              My orders
            </Link>
            <Link to="/account/profile" role="menuitem" style={menuItemStyle}>
              <UserIcon color="#000" />
              Account details
            </Link>
          </div>
          <div style={{ height: "1px", backgroundColor: "#ededed" }} />
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            style={{ ...menuItemStyle, color: "#666", marginTop: "6px" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 4h4v16h-4" />
              <path d="M10 8l-4 4 4 4M6 12h10" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
